import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';
import { capitalGoodsSpendCategories } from '/business/shared/components/questions/scope3/goods/capitalGoods/helpers/helpers';
import { purchasedGoodsAndServicesSpendCategories } from '/business/shared/components/questions/scope3/goods/purchasedGoods/helpers/helpers';
import getApplicationCurrencySelector from '/business/shared/state/selectors/application/offset/getApplicationCurrencySelector';
import getAppLanguageSelector from '/business/shared/state/selectors/language/getAppLanguageSelector';
import type { QuestionInputField, QuestionSchemaType } from '/index';
import getCurrencySymbol from '/shared/functions/getCurrencySymbol';

export default function useSpendDataSchema({ question }: { question: string }): QuestionSchemaType {
  const { t } = useTranslation();
  const currencyCode = useSelector(getApplicationCurrencySelector);
  const userLang = useSelector(getAppLanguageSelector);
  const currencySymbol = getCurrencySymbol({ currencyCode, userLang });

  const categories =
    question === 'purchasedGoods' ? purchasedGoodsAndServicesSpendCategories : capitalGoodsSpendCategories;

  const spendCategory: QuestionInputField = useMemo(() => {
    return {
      propName: 'spendCategory',
      element: 'spend_category',
      type: FIELD_TYPE_DROPDOWN,
      labelI18nKey: 'business:QuestionPage.forms.purchasedGoodsAndServices.spendData.spendCategory.title',
      placeholderI18nKey:
        'business:QuestionPage.forms.purchasedGoodsAndServices.spendData.spendCategory.selectorPlaceholder',
      values: categories,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOSPENDCATEGORY',
          errStr: t('business:Errors.purchasedGoodsAndServices.spendData.spendCategory.provide'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRSPENDCATEGORYNOTFOUND',
          errStr: t('business:Errors.purchasedGoodsAndServices.spendData.spendCategory.noCategory'),
        },
      ],
    };
  }, [t]);

  const spendValue: QuestionInputField = useMemo(() => {
    return {
      propName: 'spendValue',
      element: 'spendValue',
      type: FIELD_TYPE_NUMBER,
      labelI18nKey: 'business:QuestionPage.forms.purchasedGoodsAndServices.spendData.spendTotal.title',
      propValue: false,
      dropdown: {
        values: [{ name: currencyCode || '', displayNameKey: currencySymbol || '' }],
        propName: 'currencySymbol',
        element: 'currency_symbol',
        dropdownFirst: true,
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.purchasedGoodsAndServices.spendData.spendValue.provide'),
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          value: 99999999,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.purchasedGoodsAndServices.spendData.spendValue.tooHigh'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          value: 0.1,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.purchasedGoodsAndServices.spendData.spendValue.tooLow'),
        },
      ],
    };
  }, [currencySymbol, currencyCode, t]);

  const dataSource = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      element: 'data_source',
      labelI18nKey: 'business:QuestionPage.forms.refrigerants.para6',
      description: <></>,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [currencySymbol, currencyCode, t]);

  const pageSchema = useMemo(
    () => [spendCategory, spendValue, dataSource] as QuestionInputField[],
    [spendCategory, spendValue, dataSource],
  );
  return {
    pageSchema,
  };
}
